import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  formatLegislationTypeLabel,
  type LegislationTypeCode,
} from '@artem-lex/common-server/radar/legislationTypes'
import { Check, CircleHelp } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient } from '@/shared/api/apiClient'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import {
  useAddScopeTierTags,
  useRemoveScopeTierTags,
  useScopeTierTags,
  type ScopeTierDocumentDetailResponse,
  useClearExpertScopeSpan,
  useResolveExtensionReview,
  useRetryScopeTierMaterialization,
  useSaveExpertScopeSpan,
  useScopeTierDocument,
  useScopeTierDocuments,
  useSetScopeTierStatus,
  useSetScopeTierValue,
} from '@/shared/hooks/api/radarScopeTier'
import { formatLegislationCorpusLabel } from '@/features/radar-scope-tier/utils/consolidationProvenance'
import { getApiErrorMessage } from '@/shared/utils/getApiErrorMessage'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import LegislationTypeFilter from '../components/LegislationTypeFilter'
import AssignTierBeforeApprovalDialog from '../components/AssignTierBeforeApprovalDialog'
import {
  EXPERT_SPAN_REQUIRED_FOR_APPROVAL_MESSAGE,
  requiresExpertSpanBeforeApproval,
  shouldPromptForTierBeforeApproval,
} from '../utils/approvalRequirements'
import {
  formatEmbeddingReleaseStatusLabel,
  shouldShowRetryMaterializationButton,
} from '../utils/embeddingReleaseStatus'
import {
  collectEurLexUrlsForCelexItems,
  openEurLexTabsForCelexItems,
} from '../utils/eurLexTabs'
import { openEurLexUrlsViaExtension } from '../utils/extensionBridge'
import { resolveActiveSpanBlocks } from '../utils/highlightPriority'
import { RADAR_SCOPE_TIER_BATCH_PATH } from '../utils/routes'
import {
  isOpenTagMenuShortcut,
  parseTierShortcutKey,
  shouldAdvanceToNextDocument,
} from '../utils/shortcuts'
import {
  applyScopeTierUrlFilters,
  parseScopeTierUrlFilters,
  shouldRewriteTypesParam,
} from '../utils/urlFilters'

const POLL_INTERVAL_MS = 15000
const DOCUMENT_LIST_PAGE_SIZE = 50
const DOCUMENT_DETAIL_PREFETCH_COUNT = 10
const SEARCH_DEBOUNCE_MS = 300
const SHORTCUTS: Array<{ key: string; description: string }> = [
  { key: 'a', description: 'Move to the previous document in the list' },
  { key: 'd', description: 'Move to the next document in the list' },
  { key: 'w', description: 'Toggle approved/pending status' },
  { key: 'f', description: 'Flag current document for later review' },
  { key: 't', description: 'Trash current document as irrelevant' },
  { key: '1 / 2 / 3', description: 'Set the current document scope tier' },
  { key: 's', description: 'Jump to the next "scope" mention in the document' },
  { key: 'c', description: 'Clear the current expert-selected span' },
  { key: 'ctrl + alt + i', description: 'Open the tag selection menu' },
]

export default function RadarScopeTierPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { data: session, isFetched: isSessionFetched } = useGetUserSession()
  const queryClient = useQueryClient()

  const tagsParam = params.get('tags') ?? ''
  const filters = useMemo(() => parseScopeTierUrlFilters(params), [params])
  const query = filters.query
  const selectedDocumentId = params.get('documentId')
  const includeUnassigned = filters.includeUnassigned
  const inForceFilter = filters.inForceFilter
  const status = filters.status
  const includeTrashed = filters.includeTrashed
  const [searchInput, setSearchInput] = useState(query)
  const [isFilterTagsMenuOpen, setIsFilterTagsMenuOpen] = useState(false)
  const [filterTagsQuery, setFilterTagsQuery] = useState('')
  const filterTagsApiQuery = useScopeTierTags({
    query: filterTagsQuery,
    limit: 200,
  })
  const selectedTiers = filters.tiers
  const selectedTypes = filters.types

  const tiers = selectedTiers
  const selectedTags = useMemo(
    () =>
      Array.from(
        new Set(
          tagsParam
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value.length > 0)
        )
      ),
    [tagsParam]
  )

  const listQuery = useScopeTierDocuments(
    {
      q: query || undefined,
      tiers,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      types: selectedTypes.length > 0 ? selectedTypes : undefined,
      includeUnassigned: includeUnassigned || undefined,
      inForceFilter,
      status,
      includeTrashed,
      limit: DOCUMENT_LIST_PAGE_SIZE,
    },
    {
      refetchInterval: POLL_INTERVAL_MS,
      enabled: isSessionFetched && !!session?.isArtemAdmin,
    }
  )

  const listItems = useMemo(() => {
    const pages = listQuery.data?.pages ?? []
    const seen = new Set<string>()
    const items = []
    for (const page of pages) {
      for (const item of page.items) {
        if (seen.has(item.documentId)) {
          continue
        }
        seen.add(item.documentId)
        items.push(item)
      }
    }
    return items
  }, [listQuery.data?.pages])

  const firstPage = listQuery.data?.pages?.[0]
  const tierCounts = firstPage?.tierCounts ?? { 1: 0, 2: 0, 3: 0 }
  const unassignedCount = firstPage?.unassignedCount ?? 0
  const globalPendingCount = firstPage?.globalPendingCount ?? 0
  const extensionReviewNeededCount = firstPage?.extensionReviewNeededCount ?? 0
  const consolidationReviewNeededCount = firstPage?.consolidationReviewNeededCount ?? 0

  const listSelection = useMemo(() => {
    const selected = selectedDocumentId ?? listItems[0]?.documentId ?? null
    const selectedIndex = selected
      ? listItems.findIndex((item) => item.documentId === selected)
      : -1
    return {
      selectedDocumentId: selected,
      prevDocumentId:
        selectedIndex > 0
          ? (listItems[selectedIndex - 1]?.documentId ?? null)
          : null,
      nextDocumentId:
        selectedIndex >= 0 && selectedIndex < listItems.length - 1
          ? (listItems[selectedIndex + 1]?.documentId ?? null)
          : null,
    }
  }, [listItems, selectedDocumentId])

  const effectiveDocumentId = listSelection.selectedDocumentId
  const detailQuery = useScopeTierDocument(effectiveDocumentId)

  const prefetchDocumentIds = useMemo(() => {
    if (listItems.length === 0) {
      return []
    }
    const selectedIndex = effectiveDocumentId
      ? listItems.findIndex((item) => item.documentId === effectiveDocumentId)
      : -1
    const anchorIndex = selectedIndex >= 0 ? selectedIndex : 0
    const start = Math.max(
      0,
      anchorIndex - Math.floor(DOCUMENT_DETAIL_PREFETCH_COUNT / 2)
    )
    const end = Math.min(
      listItems.length,
      start + DOCUMENT_DETAIL_PREFETCH_COUNT
    )
    return listItems
      .slice(start, end)
      .map((item) => item.documentId)
      .filter((documentId) => documentId !== effectiveDocumentId)
  }, [effectiveDocumentId, listItems])

  useEffect(() => {
    for (const documentId of prefetchDocumentIds) {
      void queryClient.prefetchQuery<ScopeTierDocumentDetailResponse>({
        queryKey: ['radar-scope-tier', 'document', documentId],
        queryFn: async () => {
          const response = await apiClient.get<ScopeTierDocumentDetailResponse>(
            `/radar/scope-tier/documents/${documentId}`
          )
          return response.data
        },
        staleTime: POLL_INTERVAL_MS,
      })
    }
  }, [prefetchDocumentIds, queryClient])

  const statusMutation = useSetScopeTierStatus(effectiveDocumentId)
  const tierMutation = useSetScopeTierValue(effectiveDocumentId)
  const saveSpanMutation = useSaveExpertScopeSpan(effectiveDocumentId)
  const clearSpanMutation = useClearExpertScopeSpan(effectiveDocumentId)
  const addTagsMutation = useAddScopeTierTags(effectiveDocumentId)
  const removeTagsMutation = useRemoveScopeTierTags(effectiveDocumentId)
  const resolveExtensionReviewMutation =
    useResolveExtensionReview(effectiveDocumentId)
  const retryMaterializationMutation =
    useRetryScopeTierMaterialization(effectiveDocumentId)

  const [changedByDocumentId, setChangedByDocumentId] = useState<
    Record<string, boolean>
  >({})
  const [hideHighlightsByDocumentId, setHideHighlightsByDocumentId] = useState<
    Record<string, boolean>
  >({})
  const [isShortcutsDialogOpen, setIsShortcutsDialogOpen] = useState(false)
  const [isTierRequiredDialogOpen, setIsTierRequiredDialogOpen] = useState(false)
  const [advanceAfterApproval, setAdvanceAfterApproval] = useState(false)
  const [approvalBlockedMessage, setApprovalBlockedMessage] = useState<
    string | null
  >(null)
  const [isTagMenuOpen, setIsTagMenuOpen] = useState(false)
  const [tagMenuQuery, setTagMenuQuery] = useState('')
  const tagsQuery = useScopeTierTags({ query: tagMenuQuery, limit: 100 })
  const [materializationFeedback, setMaterializationFeedback] = useState<
    string | null
  >(null)
  const [tabOpenFeedback, setTabOpenFeedback] = useState<{
    requested: number
    opened: number
    skipped: number
    blocked: number
  } | null>(null)
  const lastVersionByDocumentId = useRef<Record<string, string>>({})
  const contentPanelRef = useRef<HTMLElement | null>(null)
  const mainPanelScrollRef = useRef<HTMLElement | null>(null)
  const scopeMatchRefs = useRef<Array<HTMLElement | null>>([])
  const activeScopeMatchIndexRef = useRef(-1)
  const [activeScopeMatchIndex, setActiveScopeMatchIndex] = useState(-1)
  const tagMenuInputRef = useRef<HTMLInputElement | null>(null)
  const filterTagsInputRef = useRef<HTMLInputElement | null>(null)

  const filterTagOptions = useMemo(() => {
    const byNormalized = new Map<string, { name: string; normalized: string }>()
    for (const tag of filterTagsApiQuery.data?.items ?? []) {
      byNormalized.set(tag.normalized.toLowerCase(), tag)
    }
    for (const tag of selectedTags) {
      const normalized = tag.toLowerCase()
      if (!byNormalized.has(normalized)) {
        byNormalized.set(normalized, { name: tag, normalized })
      }
    }
    return Array.from(byNormalized.values()).sort((left, right) =>
      left.name.localeCompare(right.name)
    )
  }, [filterTagsApiQuery.data?.items, selectedTags])

  useEffect(() => {
    if (session && !session.isArtemAdmin) {
      navigate('/', { replace: true })
    }
  }, [session, navigate])

  useEffect(() => {
    const rawTypes = params.get('types')
    if (!shouldRewriteTypesParam(rawTypes, filters.types)) {
      return
    }
    const next = applyScopeTierUrlFilters(new URLSearchParams(params), filters)
    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true })
    }
  }, [filters, params, setParams])

  useEffect(() => {
    if (listItems.length === 0) {
      return
    }
    const nextChanged: Record<string, boolean> = {}
    const nextVersions: Record<string, string> = {
      ...lastVersionByDocumentId.current,
    }
    for (const item of listItems) {
      const previousVersion = lastVersionByDocumentId.current[item.documentId]
      if (previousVersion && previousVersion !== item.version) {
        nextChanged[item.documentId] = true
      }
      nextVersions[item.documentId] = item.version
    }
    lastVersionByDocumentId.current = nextVersions
    if (Object.keys(nextChanged).length > 0) {
      setChangedByDocumentId((prev) => ({ ...prev, ...nextChanged }))
    }
  }, [listItems])

  useEffect(() => {
    if (!listQuery.data || selectedDocumentId) {
      return
    }
    const nextSelected = listSelection.selectedDocumentId
    if (!nextSelected) {
      return
    }
    const next = new URLSearchParams(params)
    next.set('documentId', nextSelected)
    setParams(next, { replace: true })
  }, [
    listQuery.data,
    listSelection.selectedDocumentId,
    params,
    selectedDocumentId,
    setParams,
  ])

  const selectDocument = useCallback(
    (documentId: string) => {
      const next = new URLSearchParams(params)
      next.set('documentId', documentId)
      setParams(next)
      setChangedByDocumentId((prev) => ({ ...prev, [documentId]: false }))
    },
    [params, setParams]
  )

  const goToNextDocument = useCallback(() => {
    if (listSelection.nextDocumentId) {
      selectDocument(listSelection.nextDocumentId)
    }
  }, [listSelection.nextDocumentId, selectDocument])

  const commitApproval = useCallback(
    async (options?: { advanceAfter?: boolean }) => {
      await statusMutation.mutateAsync('approved')
      if (options?.advanceAfter) {
        goToNextDocument()
      }
    },
    [goToNextDocument, statusMutation]
  )

  const requestApproval = useCallback(
    (options?: { advanceAfter?: boolean }) => {
      if (!detailQuery.data) {
        return
      }

      if (detailQuery.data.review.status === 'approved') {
        setApprovalBlockedMessage(null)
        statusMutation.mutate('pending')
        return
      }

      if (
        shouldPromptForTierBeforeApproval({
          reviewStatus: detailQuery.data.review.status,
          currentTier: detailQuery.data.tierState.currentTier,
        })
      ) {
        setAdvanceAfterApproval(options?.advanceAfter ?? false)
        setIsTierRequiredDialogOpen(true)
        return
      }

      if (
        requiresExpertSpanBeforeApproval({
          reviewStatus: detailQuery.data.review.status,
          currentTier: detailQuery.data.tierState.currentTier,
          scopeTiers: detailQuery.data.spans.map((span) => span.scopeTier),
          expertSpan: detailQuery.data.expertSpan,
        })
      ) {
        setApprovalBlockedMessage(EXPERT_SPAN_REQUIRED_FOR_APPROVAL_MESSAGE)
        return
      }

      setApprovalBlockedMessage(null)
      void commitApproval(options)
    },
    [commitApproval, detailQuery.data, statusMutation]
  )

  const approveWithTier = useCallback(
    async (tier: 1 | 2 | 3) => {
      if (!detailQuery.data) {
        return
      }

      if (
        requiresExpertSpanBeforeApproval({
          reviewStatus: detailQuery.data.review.status,
          currentTier: detailQuery.data.tierState.currentTier,
          scopeTiers: detailQuery.data.spans.map((span) => span.scopeTier),
          expertSpan: detailQuery.data.expertSpan,
          approvingTier: tier,
        })
      ) {
        setApprovalBlockedMessage(EXPERT_SPAN_REQUIRED_FOR_APPROVAL_MESSAGE)
        return
      }

      setApprovalBlockedMessage(null)
      setIsTierRequiredDialogOpen(false)
      try {
        await tierMutation.mutateAsync(tier)
        await commitApproval({ advanceAfter: advanceAfterApproval })
      } finally {
        setAdvanceAfterApproval(false)
      }
    },
    [advanceAfterApproval, commitApproval, detailQuery.data, tierMutation]
  )

  const onToggleApproval = () => {
    requestApproval()
  }

  const onRetryMaterialization = async () => {
    setMaterializationFeedback(null)
    try {
      const result = await retryMaterializationMutation.mutateAsync()
      if (result.status === 'queued') {
        setMaterializationFeedback('Re-embedding queued. Status will update shortly.')
        return
      }
      setMaterializationFeedback(
        result.reason
          ? `Retry skipped: ${result.reason}`
          : 'Retry skipped for this document.'
      )
    } catch (error) {
      setMaterializationFeedback(getApiErrorMessage(error))
    }
  }

  const updateFilter = (next: Record<string, string | null | undefined>) => {
    const updated = new URLSearchParams(params)
    for (const [key, value] of Object.entries(next)) {
      if (value == null || value === '') {
        updated.delete(key)
      } else {
        updated.set(key, value)
      }
    }
    updated.delete('cursor')
    setParams(updated)
  }

  useEffect(() => {
    setSearchInput(query)
  }, [query])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (searchInput === query) {
        return
      }
      updateFilter({ q: searchInput })
    }, SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [query, searchInput])

  useEffect(() => {
    if (!isFilterTagsMenuOpen) {
      setFilterTagsQuery('')
      return
    }
    const timeoutId = window.setTimeout(() => {
      filterTagsInputRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(timeoutId)
  }, [isFilterTagsMenuOpen])

  const toggleFilterTag = (tag: string) => {
    const hasTag = selectedTags.some(
      (value) => value.toLowerCase() === tag.toLowerCase()
    )
    const nextTags = hasTag
      ? selectedTags.filter(
          (value) => value.toLowerCase() !== tag.toLowerCase()
        )
      : [...selectedTags, tag]
    updateFilter({
      tags: nextTags.length > 0 ? nextTags.join(',') : null,
    })
  }

  const toggleFilterType = (typeCode: LegislationTypeCode) => {
    const nextTypes = selectedTypes.includes(typeCode)
      ? selectedTypes.filter((value) => value !== typeCode)
      : [...selectedTypes, typeCode]
    updateFilter({
      types: nextTypes.length > 0 ? nextTypes.join(',') : null,
    })
  }

  const toggleTier = (tier: 1 | 2 | 3) => {
    const isSelected = selectedTiers.includes(tier)
    if (isSelected && selectedTiers.length === 1 && !includeUnassigned) {
      return
    }

    const nextTiers = isSelected
      ? selectedTiers.filter((value) => value !== tier)
      : ([...selectedTiers, tier].sort() as Array<1 | 2 | 3>)

    updateFilter({
      tiers: encodeTierSelection(nextTiers),
    })
  }

  const toggleUnassigned = () => {
    const next = !includeUnassigned
    if (!next && selectedTiers.length === 0) {
      return
    }
    updateFilter({
      includeUnassigned: next ? 'true' : null,
    })
  }

  const onSaveSelection = async () => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return
    }
    const range = selection.getRangeAt(0)
    const startNode =
      range.startContainer.parentElement?.closest('[data-block-id]')
    const endNode = range.endContainer.parentElement?.closest('[data-block-id]')
    const selectedText = selection.toString().trim()
    if (!startNode || !endNode || !selectedText || !detailQuery.data) {
      return
    }
    const startBlockId = startNode.getAttribute('data-block-id')
    const endBlockId = endNode.getAttribute('data-block-id')
    if (!startBlockId || !endBlockId) {
      return
    }
    const startCharOffset = computeOffsetWithinBlock(
      startNode,
      range.startContainer,
      range.startOffset
    )
    const endCharOffset = computeOffsetWithinBlock(
      endNode,
      range.endContainer,
      range.endOffset
    )
    if (startCharOffset == null || endCharOffset == null) {
      return
    }
    const currentExpertSpan = detailQuery.data.expertSpan
    if (
      currentExpertSpan &&
      currentExpertSpan.startBlockId === startBlockId &&
      currentExpertSpan.endBlockId === endBlockId &&
      (currentExpertSpan.startCharOffset ?? null) === startCharOffset &&
      (currentExpertSpan.endCharOffset ?? null) === endCharOffset &&
      currentExpertSpan.selectedText.trim() === selectedText
    ) {
      return
    }

    await saveSpanMutation.mutateAsync({
      startBlockId,
      endBlockId,
      startCharOffset,
      endCharOffset,
      selectedText,
      normalizedCelex: detailQuery.data.document.celex,
    })
    setApprovalBlockedMessage(null)
    setHideHighlightsByDocumentId((prev) => ({
      ...prev,
      [detailQuery.data.document.documentId]: false,
    }))
  }

  const onContentMouseUp = () => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return
    }
    if (!contentPanelRef.current?.contains(selection.anchorNode)) {
      return
    }
    if (!contentPanelRef.current?.contains(selection.focusNode)) {
      return
    }
    void onSaveSelection()
  }

  const clearContentSelection = () => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return
    }
    if (!contentPanelRef.current?.contains(selection.anchorNode)) {
      return
    }
    if (!contentPanelRef.current?.contains(selection.focusNode)) {
      return
    }
    selection.removeAllRanges()
  }

  const jumpToNextScopeMatch = () => {
    const scopeMatches = scopeMatchRefs.current.filter(
      (node): node is HTMLElement => node !== null
    )
    if (scopeMatches.length === 0) {
      return
    }

    const nextIndex =
      (activeScopeMatchIndexRef.current + 1) % scopeMatches.length
    activeScopeMatchIndexRef.current = nextIndex
    setActiveScopeMatchIndex(nextIndex)
    const nextMatch = scopeMatches[nextIndex]
    if (!nextMatch) {
      return
    }

    const container = mainPanelScrollRef.current
    if (!container) {
      nextMatch.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }

    const containerRect = container.getBoundingClientRect()
    const targetRect = nextMatch.getBoundingClientRect()
    const topWithinContainer =
      targetRect.top - containerRect.top + container.scrollTop
    const targetScrollTop = Math.max(
      0,
      topWithinContainer - container.clientHeight / 3
    )
    container.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth',
    })
  }

  const onClearSpan = async () => {
    if (!detailQuery.data) {
      return
    }
    const documentId = detailQuery.data.document.documentId
    await clearSpanMutation.mutateAsync(detailQuery.data.expertSpan?.spanId)
    setHideHighlightsByDocumentId((prev) => ({
      ...prev,
      [documentId]: true,
    }))
  }

  const onOpenEurLexTabs = (limit: number) => {
    void (async () => {
      const collected = collectEurLexUrlsForCelexItems(listItems, limit)
      const extensionResult = await openEurLexUrlsViaExtension(collected.urls)

      if (extensionResult) {
        setTabOpenFeedback({
          requested: limit,
          opened: extensionResult.opened,
          skipped: collected.skipped,
          blocked: extensionResult.failed,
        })
        return
      }

      const fallback = openEurLexTabsForCelexItems(listItems, limit)
      setTabOpenFeedback({
        requested: limit,
        opened: fallback.opened,
        skipped: fallback.skipped,
        blocked: fallback.blocked,
      })
    })()
  }

  const onCreateTagFromMenu = async () => {
    const normalized = normalizeTagInput(tagMenuQuery)
    if (!normalized) {
      return
    }
    await addTagsMutation.mutateAsync({ tags: [normalized] })
    setTagMenuQuery('')
    setIsTagMenuOpen(false)
  }

  const onRemoveTag = async (tag: string) => {
    await removeTagsMutation.mutateAsync({ tags: [tag] })
  }

  const normalizedTagDraft = normalizeTagInput(tagMenuQuery)
  const tagOptions = tagsQuery.data?.items ?? []
  const normalizedExistingTagNames = new Set(
    tagOptions.map((item) => item.normalized)
  )
  const canCreateTag =
    normalizedTagDraft != null &&
    !normalizedExistingTagNames.has(normalizedTagDraft.toLowerCase())

  const onSelectExistingTag = async (tag: string) => {
    await addTagsMutation.mutateAsync({ tags: [tag] })
    setTagMenuQuery('')
    setIsTagMenuOpen(false)
  }

  useEffect(() => {
    if (!isTagMenuOpen) {
      setTagMenuQuery('')
      return
    }
    const timeoutId = window.setTimeout(() => {
      tagMenuInputRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(timeoutId)
  }, [isTagMenuOpen])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!listQuery.data) {
        return
      }
      const key = event.key.toLowerCase()
      const code = event.code
      if (isOpenTagMenuShortcut(event)) {
        event.preventDefault()
        setIsTagMenuOpen(true)
        return
      }
      const target = event.target as HTMLElement | null
      const isTypingTarget =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      if (isTypingTarget) {
        return
      }
      if (key === 'a' && listSelection.prevDocumentId) {
        selectDocument(listSelection.prevDocumentId)
      } else if (key === 'd' && listSelection.nextDocumentId) {
        selectDocument(listSelection.nextDocumentId)
      } else if (key === 'w' && detailQuery.data) {
        event.preventDefault()
        requestApproval({
          advanceAfter: shouldAdvanceToNextDocument(key),
        })
      } else if (key === 'f' && detailQuery.data) {
        event.preventDefault()
        statusMutation.mutate('flagged')
        if (shouldAdvanceToNextDocument(key)) {
          goToNextDocument()
        }
      } else if (key === 't' && detailQuery.data) {
        event.preventDefault()
        statusMutation.mutate('trashed')
        if (shouldAdvanceToNextDocument(key)) {
          goToNextDocument()
        }
      } else if (key === 's') {
        event.preventDefault()
        jumpToNextScopeMatch()
      } else if (detailQuery.data && parseTierShortcutKey(key) != null) {
        const nextTier = parseTierShortcutKey(key) as 1 | 2 | 3
        event.preventDefault()
        clearContentSelection()
        if (isTierRequiredDialogOpen) {
          void approveWithTier(nextTier)
        } else {
          tierMutation.mutate(nextTier)
        }
      } else if (key === 'c' || code === 'KeyC') {
        event.preventDefault()
        void onClearSpan()
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [
    approveWithTier,
    detailQuery.data,
    goToNextDocument,
    isTierRequiredDialogOpen,
    listQuery.data,
    listSelection.nextDocumentId,
    listSelection.prevDocumentId,
    requestApproval,
    selectDocument,
    statusMutation,
    tierMutation,
  ])

  const highlightedBlockIds = useMemo(() => {
    const blocks = detailQuery.data?.blocks ?? []
    const spans = detailQuery.data?.spans ?? []
    const byId = new Map(
      blocks.map((block) => [block.blockId, block.blockOrder])
    )
    const result = new Set<string>()
    for (const span of spans) {
      const startOrder = byId.get(span.startBlockId)
      const endOrder = byId.get(span.endBlockId)
      if (startOrder == null || endOrder == null) {
        continue
      }
      const min = Math.min(startOrder, endOrder)
      const max = Math.max(startOrder, endOrder)
      for (const block of blocks) {
        if (block.blockOrder >= min && block.blockOrder <= max) {
          result.add(block.blockId)
        }
      }
    }
    return result
  }, [detailQuery.data?.blocks, detailQuery.data?.spans])

  const expertHighlightedBlockIds = useMemo(() => {
    const blocks = detailQuery.data?.blocks ?? []
    const expertSpan = detailQuery.data?.expertSpan
    if (!expertSpan) {
      return new Set<string>()
    }

    const byId = new Map(
      blocks.map((block) => [block.blockId, block.blockOrder])
    )
    const startOrder = byId.get(expertSpan.startBlockId)
    const endOrder = byId.get(expertSpan.endBlockId)
    if (startOrder == null || endOrder == null) {
      return new Set<string>()
    }

    const min = Math.min(startOrder, endOrder)
    const max = Math.max(startOrder, endOrder)
    const result = new Set<string>()
    for (const block of blocks) {
      if (block.blockOrder >= min && block.blockOrder <= max) {
        result.add(block.blockId)
      }
    }
    return result
  }, [detailQuery.data?.blocks, detailQuery.data?.expertSpan])

  const effectiveHighlightedBlockIds = useMemo(() => {
    if (
      effectiveDocumentId &&
      hideHighlightsByDocumentId[effectiveDocumentId]
    ) {
      return new Set<string>()
    }
    const hasExpertSpan = Boolean(
      detailQuery.data?.expertSpan?.selectedText?.trim()
    )
    const shouldUseTierThreeTitleOnlyHighlight =
      detailQuery.data?.tierState.currentTier === 3 && !hasExpertSpan
    if (shouldUseTierThreeTitleOnlyHighlight) {
      const titleBlockId = detailQuery.data?.blocks?.[0]?.blockId
      return titleBlockId ? new Set([titleBlockId]) : new Set<string>()
    }
    return resolveActiveSpanBlocks({
      activeSpanSource: detailQuery.data?.tierState.activeSpanSource,
      expertBlocks: expertHighlightedBlockIds,
      spanBlocks: highlightedBlockIds,
    })
  }, [
    detailQuery.data?.tierState.activeSpanSource,
    effectiveDocumentId,
    expertHighlightedBlockIds,
    hideHighlightsByDocumentId,
    highlightedBlockIds,
  ])

  const shouldShowScopeKeywordMarks = !detailQuery.data?.extensionReviewCapture

  const keywordDecoratedBlocks = useMemo(() => {
    const blocks = detailQuery.data?.blocks ?? []
    let scopeMatchIndex = 0

    const decorated = blocks.map((block) => {
      const segments = splitByKeywordMatches(block.rawText)
      const mapped = segments.map((segment) => {
        if (segment.kind === 'scope') {
          const next = {
            ...segment,
            scopeMatchIndex,
          }
          scopeMatchIndex += 1
          return next
        }
        return segment
      })
      return {
        blockId: block.blockId,
        segments: mapped,
      }
    })

    const scopeCount = decorated.reduce(
      (acc, entry) =>
        acc +
        entry.segments.filter((segment) => segment.kind === 'scope').length,
      0
    )
    const subjectMatterCount = decorated.reduce(
      (acc, entry) =>
        acc +
        entry.segments.filter((segment) => segment.kind === 'subjectMatter')
          .length,
      0
    )

    return {
      blocksById: new Map(
        decorated.map((entry) => [entry.blockId, entry.segments])
      ),
      scopeCount,
      subjectMatterCount,
      totalMentions: scopeCount + subjectMatterCount,
    }
  }, [detailQuery.data?.blocks])

  useEffect(() => {
    activeScopeMatchIndexRef.current = -1
    scopeMatchRefs.current = []
    setActiveScopeMatchIndex(-1)
  }, [effectiveDocumentId, keywordDecoratedBlocks.scopeCount])

  if (session && !session.isArtemAdmin) {
    return null
  }

  if (!isSessionFetched || listQuery.isLoading) {
    return (
      <div className='flex h-full min-h-svh flex-col items-center justify-center text-muted-foreground'>
        Loading…
      </div>
    )
  }

  if (listQuery.isError) {
    return (
      <div className='flex h-full min-h-svh flex-col items-center justify-center gap-2 px-6 text-center text-muted-foreground'>
        <p>Unable to load radar scope-tier data right now.</p>
        <p className='text-sm'>{getApiErrorMessage(listQuery.error)}</p>
      </div>
    )
  }

  // min-h-0 on flex ancestors lets the sidebar scroll and keeps Load more reachable.
  return (
    <div className='flex h-full min-h-0 flex-1 flex-col'>
      <main className='flex min-h-0 flex-1 overflow-hidden'>
        <aside className='flex min-h-0 w-[340px] shrink-0 flex-col overflow-hidden border-r'>
          <div className='min-h-0 flex-1 space-y-3 overflow-y-auto p-4'>
            <div className='flex items-center justify-between gap-2'>
              <h2 className='text-xl font-semibold'>Radar Scope Tier Viewer</h2>
              <button
                type='button'
                aria-label='Open keyboard shortcuts help'
                title='Keyboard shortcuts'
                className='inline-flex items-center justify-center rounded-full p-1 text-muted-foreground hover:bg-accent hover:text-foreground'
                onClick={() => setIsShortcutsDialogOpen(true)}
              >
                <CircleHelp className='size-5' aria-hidden />
              </button>
            </div>
            <Input
              value={searchInput}
              placeholder='Search CELEX'
              onChange={(event) => setSearchInput(event.target.value)}
            />

            <Popover
              open={isFilterTagsMenuOpen}
              onOpenChange={setIsFilterTagsMenuOpen}
            >
              <PopoverTrigger asChild>
                <button
                  type='button'
                  className='flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'
                >
                  <span
                    className={
                      selectedTags.length > 0 ? '' : 'text-muted-foreground'
                    }
                  >
                    {selectedTags.length > 0
                      ? `Filter tags (${selectedTags.length} selected)`
                      : 'Filter tags'}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent align='start' className='w-[320px] p-0'>
                <Command shouldFilter={false}>
                  <CommandInput
                    ref={filterTagsInputRef}
                    placeholder='Search tags'
                    value={filterTagsQuery}
                    onValueChange={setFilterTagsQuery}
                  />
                  <CommandList className='max-h-56'>
                    <CommandEmpty>No tags found</CommandEmpty>
                    {filterTagOptions.map((tag) => {
                      const isSelected = selectedTags.some(
                        (value) =>
                          value.toLowerCase() === tag.name.toLowerCase()
                      )
                      return (
                        <CommandItem
                          key={tag.normalized}
                          value={tag.name}
                          onSelect={() => toggleFilterTag(tag.name)}
                        >
                          <span className='flex-1'>{tag.name}</span>
                          {isSelected ? (
                            <Check className='size-4 text-muted-foreground' />
                          ) : null}
                        </CommandItem>
                      )
                    })}
                  </CommandList>
                </Command>
                {selectedTags.length > 0 ? (
                  <div className='border-t p-2'>
                    <button
                      type='button'
                      className='text-xs underline'
                      onClick={() => updateFilter({ tags: null })}
                    >
                      Clear tag filters
                    </button>
                  </div>
                ) : null}
              </PopoverContent>
            </Popover>
            <div className='grid grid-cols-2 gap-2'>
              <div className='col-span-2 rounded border px-2 py-1'>
                <div className='mb-1 text-xs text-muted-foreground'>
                  Include tiers
                </div>
                <div className='flex flex-wrap gap-3'>
                  {[1, 2, 3].map((tierValue) => (
                    <label
                      key={tierValue}
                      className='flex items-center gap-1 text-sm'
                    >
                      <input
                        type='checkbox'
                        checked={selectedTiers.includes(tierValue as 1 | 2 | 3)}
                        onChange={() => toggleTier(tierValue as 1 | 2 | 3)}
                      />
                      <span>{`Tier ${tierValue} (${tierCounts[tierValue as 1 | 2 | 3]})`}</span>
                    </label>
                  ))}
                  <label className='flex items-center gap-1 text-sm'>
                    <input
                      type='checkbox'
                      checked={includeUnassigned}
                      onChange={toggleUnassigned}
                    />
                    <span>{`Unassigned (${unassignedCount})`}</span>
                  </label>
                </div>
              </div>
              <div className='col-span-2'>
                <LegislationTypeFilter
                  selectedTypes={selectedTypes}
                  onToggle={toggleFilterType}
                  onClear={() => updateFilter({ types: null })}
                />
              </div>
              <div className='col-span-2 flex flex-col gap-2'>
                <select
                  className='w-full rounded border bg-background px-2 py-1'
                  value={inForceFilter}
                  onChange={(event) =>
                    updateFilter({ inForceFilter: event.target.value })
                  }
                >
                  <option value='in_force'>In force only</option>
                  <option value='not_in_force'>Out of force only</option>
                  <option value='all'>Both force states</option>
                </select>
                <select
                  className='w-full rounded border bg-background px-2 py-1'
                  value={status}
                  onChange={(event) =>
                    updateFilter({ status: event.target.value })
                  }
                >
                  <option value='all'>All statuses</option>
                  <option value='extension_review_needed'>
                    {`Needs review (extension) (${extensionReviewNeededCount})`}
                  </option>
                  <option value='consolidation_review_needed'>
                    {`Needs review (consolidation) (${consolidationReviewNeededCount})`}
                  </option>
                  <option value='edited'>Edited (my recent)</option>
                  <option value='flagged'>Flagged</option>
                  <option value='trashed'>Trashed</option>
                  <option value='approved'>Approved</option>
                  <option value='pending'>{`Pending (${globalPendingCount})`}</option>
                </select>
                <span className='text-sm font-medium whitespace-nowrap'>
                  {`Pending: ${globalPendingCount}`}
                </span>
              </div>
            </div>
            <label className='flex items-center gap-2 text-sm'>
              <input
                type='checkbox'
                checked={includeTrashed}
                onChange={(event) =>
                  updateFilter({
                    includeTrashed: event.target.checked ? 'true' : null,
                  })
                }
              />
              Show trashed
            </label>
            <div className='grid grid-cols-2 gap-2'>
              <Button
                className='col-span-2'
                variant='outline'
                onClick={() => {
                  const batchParams = applyScopeTierUrlFilters(
                    new URLSearchParams(),
                    filters
                  )
                  const queryString = batchParams.toString()
                  navigate(
                    queryString
                      ? `${RADAR_SCOPE_TIER_BATCH_PATH}?${queryString}`
                      : RADAR_SCOPE_TIER_BATCH_PATH
                  )
                }}
              >
                Batch Edit Mode
              </Button>
              <Button
                variant='outline'
                onClick={() => onOpenEurLexTabs(10)}
                disabled={listItems.length === 0}
              >
                Open 10 EUR-Lex
              </Button>
              <Button
                variant='outline'
                onClick={() => onOpenEurLexTabs(20)}
                disabled={listItems.length === 0}
              >
                Open 20 EUR-Lex
              </Button>
              {tabOpenFeedback ? (
                <p className='col-span-2 text-xs text-muted-foreground'>
                  {tabOpenFeedback.opened === 0
                    ? `No tabs opened.${tabOpenFeedback.blocked > 0 ? ` Browser blocked ${tabOpenFeedback.blocked} popup${tabOpenFeedback.blocked === 1 ? '' : 's'} - allow popups for this site and retry.` : ''} ${tabOpenFeedback.skipped > 0 ? `No valid CELEX values found in the loaded list (skipped ${tabOpenFeedback.skipped}).` : ''}`.trim()
                    : `Opened ${tabOpenFeedback.opened}/${tabOpenFeedback.requested} tab${
                        tabOpenFeedback.opened === 1 ? '' : 's'
                      }${
                        tabOpenFeedback.blocked > 0
                          ? `, ${tabOpenFeedback.blocked} popup${tabOpenFeedback.blocked === 1 ? '' : 's'} blocked (allow popups for this site).`
                          : tabOpenFeedback.skipped > 0
                            ? `, skipped ${tabOpenFeedback.skipped} invalid CELEX value${tabOpenFeedback.skipped === 1 ? '' : 's'}.`
                            : '.'
                      }`}
                </p>
              ) : null}
            </div>
            <div className='space-y-1'>
              {listItems.map((item) => {
                const isSelected = item.documentId === effectiveDocumentId
                return (
                  <div
                    key={item.documentId}
                    role='button'
                    tabIndex={0}
                    className={`w-full cursor-pointer rounded-md border px-2 py-2 text-left transition-colors outline-none select-text focus-visible:ring-2 focus-visible:ring-primary/50 ${
                      isSelected
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/40'
                        : 'border-border hover:border-primary/40 hover:bg-accent/40'
                    }`}
                    onClick={(event) => {
                      if (
                        hasExpandedSelectionInsideElement(
                          window.getSelection(),
                          event.currentTarget
                        )
                      ) {
                        return
                      }
                      selectDocument(item.documentId)
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        selectDocument(item.documentId)
                      }
                    }}
                  >
                    <div
                      className={`truncate ${isSelected ? 'font-semibold' : 'font-medium'}`}
                    >
                      {toDisplayCelex(item.celex)}
                    </div>
                    <div className='mt-1 flex items-center gap-2 text-xs'>
                      <span>{formatLegislationTypeLabel(item.type)}</span>
                      <span>{formatLegislationCorpusLabel(item)}</span>
                      <span>{item.inForce ? 'In force' : 'Out of force'}</span>
                      <span>Tier {item.currentTier ?? '-'}</span>
                      <span className='capitalize'>{item.status}</span>
                      {item.scopeCarriedForward ? (
                        <span className='text-emerald-700'>Scope auto-carried</span>
                      ) : null}
                      {formatEmbeddingReleaseStatusLabel({
                        embeddingReleaseStatus: item.embeddingReleaseStatus,
                        reviewStatus: item.status,
                      }) ? (
                        <span
                          className={
                            item.embeddingReleaseStatus === 'failed'
                              ? 'text-destructive'
                              : 'text-amber-700'
                          }
                        >
                          {formatEmbeddingReleaseStatusLabel({
                            embeddingReleaseStatus: item.embeddingReleaseStatus,
                            reviewStatus: item.status,
                          })}
                        </span>
                      ) : null}
                      {isSelected && (
                        <span className='font-medium text-primary'>
                          Editing
                        </span>
                      )}
                      {changedByDocumentId[item.documentId] && (
                        <span className='text-amber-600'>Updated</span>
                      )}
                    </div>
                    {item.tags.length > 0 ? (
                      <div className='mt-1 flex flex-wrap gap-1'>
                        {item.tags.map((tag) => (
                          <span
                            key={`${item.documentId}-${tag}`}
                            className='rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground'
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
            {listQuery.hasNextPage ? (
              <Button
                className='sticky bottom-0 z-10 mt-2 w-full bg-background'
                variant='ghost'
                onClick={() => listQuery.fetchNextPage()}
                disabled={listQuery.isFetchingNextPage}
              >
                {listQuery.isFetchingNextPage ? 'Loading more...' : 'Load more'}
              </Button>
            ) : null}
          </div>
        </aside>
        <Dialog
          open={isShortcutsDialogOpen}
          onOpenChange={(open) => !open && setIsShortcutsDialogOpen(false)}
        >
          <DialogContent className='sm:max-w-md'>
            <DialogHeader>
              <DialogTitle>Keyboard shortcuts</DialogTitle>
            </DialogHeader>
            <p className='mb-3 text-sm text-muted-foreground'>
              Shortcuts available on this page:
            </p>
            <ul className='space-y-2'>
              {SHORTCUTS.map((shortcut) => (
                <li
                  key={shortcut.key}
                  className='flex items-start gap-2 text-sm'
                >
                  <span className='inline-flex min-w-16 justify-center rounded border border-input bg-muted px-2 py-0.5 font-mono text-xs'>
                    {shortcut.key}
                  </span>
                  <span className='text-muted-foreground'>
                    {shortcut.description}
                  </span>
                </li>
              ))}
            </ul>
          </DialogContent>
        </Dialog>

        <AssignTierBeforeApprovalDialog
          open={isTierRequiredDialogOpen}
          celex={detailQuery.data?.document.celex ?? ''}
          hasExpertSpan={Boolean(
            detailQuery.data?.expertSpan?.selectedText?.trim()
          )}
          blockedMessage={
            isTierRequiredDialogOpen ? approvalBlockedMessage : null
          }
          onOpenChange={(open) => {
            if (open) {
              setIsTierRequiredDialogOpen(true)
              return
            }
            setIsTierRequiredDialogOpen(false)
            setAdvanceAfterApproval(false)
            setApprovalBlockedMessage(null)
          }}
          onSelectTier={(tier) => {
            void approveWithTier(tier)
          }}
          isSubmitting={tierMutation.isPending || statusMutation.isPending}
        />

        <section
          ref={mainPanelScrollRef}
          className='min-h-0 flex-1 overflow-auto p-4'
        >
          {!effectiveDocumentId || !detailQuery.data ? (
            <p className='text-muted-foreground'>
              Select a document to preview.
            </p>
          ) : (
            <div className='space-y-4'>
              <div className='flex flex-wrap items-center gap-2'>
                <Button
                  variant='outline'
                  onClick={() =>
                    listSelection.prevDocumentId &&
                    selectDocument(listSelection.prevDocumentId)
                  }
                  disabled={!listSelection.prevDocumentId}
                >
                  Previous
                </Button>
                <Button
                  variant='outline'
                  onClick={() =>
                    listSelection.nextDocumentId &&
                    selectDocument(listSelection.nextDocumentId)
                  }
                  disabled={!listSelection.nextDocumentId}
                >
                  Next
                </Button>
                <Button variant='default' onClick={onToggleApproval}>
                  {detailQuery.data.review.status === 'approved'
                    ? 'Set Pending'
                    : 'Approve'}
                </Button>
                {approvalBlockedMessage ? (
                  <p className='w-full text-sm text-destructive'>
                    {approvalBlockedMessage}
                  </p>
                ) : null}
                <Button
                  variant='secondary'
                  onClick={() => statusMutation.mutate('flagged')}
                >
                  Flag
                </Button>
                <Button
                  variant='destructive'
                  onClick={() => statusMutation.mutate('trashed')}
                >
                  Trash
                </Button>
                <Button
                  variant='outline'
                  onClick={() => {
                    void onClearSpan()
                  }}
                >
                  Clear Span
                </Button>
                {detailQuery.data &&
                shouldShowRetryMaterializationButton({
                  embeddingReleaseStatus:
                    detailQuery.data.document.embeddingReleaseStatus,
                  reviewStatus: detailQuery.data.review.status,
                }) ? (
                  <Button
                    variant='outline'
                    onClick={() => void onRetryMaterialization()}
                    disabled={retryMaterializationMutation.isPending}
                  >
                    {retryMaterializationMutation.isPending
                      ? 'Retrying...'
                      : 'Retry re-embedding'}
                  </Button>
                ) : null}
              </div>
              {detailQuery.data &&
              formatEmbeddingReleaseStatusLabel({
                embeddingReleaseStatus:
                  detailQuery.data.document.embeddingReleaseStatus,
                reviewStatus: detailQuery.data.review.status,
              }) ? (
                <p
                  className={`text-sm ${
                    detailQuery.data.document.embeddingReleaseStatus === 'failed'
                      ? 'text-destructive'
                      : 'text-amber-700'
                  }`}
                >
                  {formatEmbeddingReleaseStatusLabel({
                    embeddingReleaseStatus:
                      detailQuery.data.document.embeddingReleaseStatus,
                    reviewStatus: detailQuery.data.review.status,
                  })}
                </p>
              ) : null}
              {materializationFeedback ? (
                <p className='text-sm text-muted-foreground'>
                  {materializationFeedback}
                </p>
              ) : null}

              <article
                ref={contentPanelRef}
                onMouseUp={onContentMouseUp}
                className='space-y-2 rounded border bg-card p-4'
              >
                <h3 className='text-xl font-bold'>
                  {detailQuery.data.document.title}
                </h3>
                <p className='text-sm text-muted-foreground'>
                  {detailQuery.data.document.originalTitle ??
                    detailQuery.data.document.title}
                </p>
                <p className='text-xs text-muted-foreground uppercase'>
                  {toDisplayCelex(detailQuery.data.document.celex)} {'-'}{' '}
                  {formatLegislationTypeLabel(detailQuery.data.document.type)}
                </p>
                <p className='text-xs text-muted-foreground'>
                  {detailQuery.data.document.inForce
                    ? 'In force legislation'
                    : 'Out-of-force legislation'}
                </p>
                <p className='text-xs text-muted-foreground'>
                  {formatLegislationCorpusLabel(detailQuery.data.document)}
                  {detailQuery.data.document.consolidatedCelex
                    ? ` · ${toDisplayCelex(detailQuery.data.document.consolidatedCelex)}`
                    : null}
                  {detailQuery.data.document.scopeCarriedForward ? (
                    <span className='ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800'>
                      Scope auto-carried
                    </span>
                  ) : null}
                </p>
                <div className='flex flex-wrap items-center gap-2'>
                  {(detailQuery.data.document.tags ?? []).map((tag) => (
                    <button
                      type='button'
                      key={tag}
                      className='rounded border border-border bg-muted px-2 py-1 text-xs text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-none'
                      onClick={() => {
                        void onRemoveTag(tag)
                      }}
                    >
                      {tag} x
                    </button>
                  ))}
                </div>
                <div className='flex items-center gap-2'>
                  <Popover open={isTagMenuOpen} onOpenChange={setIsTagMenuOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type='button'
                        className='flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'
                        onClick={() => setIsTagMenuOpen(true)}
                      >
                        <span className='text-muted-foreground'>
                          Search or create tag
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align='start' className='w-80 p-0'>
                      <Command shouldFilter={false}>
                        <CommandInput
                          ref={tagMenuInputRef}
                          placeholder='Search or create tag'
                          value={tagMenuQuery}
                          onValueChange={setTagMenuQuery}
                        />
                        <CommandList>
                          <CommandEmpty>No matching tags</CommandEmpty>
                          {canCreateTag ? (
                            <CommandItem
                              key={`create-${normalizedTagDraft}`}
                              value={normalizedTagDraft ?? ''}
                              onSelect={() => {
                                void onCreateTagFromMenu()
                              }}
                            >
                              Create "{normalizedTagDraft}"
                            </CommandItem>
                          ) : null}
                          {tagOptions.map((tag) => (
                            <CommandItem
                              key={tag.normalized}
                              value={tag.name}
                              onSelect={() => {
                                void onSelectExistingTag(tag.name)
                              }}
                            >
                              <span className='flex-1'>{tag.name}</span>
                              {(detailQuery.data.document.tags ?? []).includes(
                                tag.name
                              ) ? (
                                <Check className='size-4 text-muted-foreground' />
                              ) : null}
                            </CommandItem>
                          ))}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <p className='text-base font-medium'>
                  {`Mentions: scope ${keywordDecoratedBlocks.scopeCount}, subject matter ${keywordDecoratedBlocks.subjectMatterCount}, total ${keywordDecoratedBlocks.totalMentions}`}
                </p>
                <p className='text-sm font-medium text-muted-foreground'>
                  {keywordDecoratedBlocks.scopeCount > 0
                    ? `Scope match ${activeScopeMatchIndex >= 0 ? activeScopeMatchIndex + 1 : 0}/${keywordDecoratedBlocks.scopeCount} (press s for next)`
                    : 'No scope matches in this document'}
                </p>
                {detailQuery.data.extensionReviewCapture ? (
                  <div className='space-y-2 rounded border border-amber-300/80 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/30'>
                    <div>
                      <p className='font-semibold text-amber-900 dark:text-amber-100'>
                        Extension capture needs manual review
                      </p>
                      <p className='mt-1 text-muted-foreground'>
                        {`Reason: ${detailQuery.data.extensionReviewCapture.reason}. `}
                        {detailQuery.data.extensionReviewCapture.sourceUrl ? (
                          <a
                            href={
                              detailQuery.data.extensionReviewCapture.sourceUrl
                            }
                            target='_blank'
                            rel='noreferrer'
                            className='underline'
                          >
                            Source page
                          </a>
                        ) : null}
                      </p>
                      <p className='mt-2 text-xs text-muted-foreground'>
                        The article below shows the current persisted blocks for
                        this document (updated when the extension sends HTML).
                        Highlights refresh when the list shows Updated or you
                        switch away and back.
                      </p>
                      <div className='mt-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() =>
                            resolveExtensionReviewMutation.mutate()
                          }
                          disabled={resolveExtensionReviewMutation.isPending}
                        >
                          {resolveExtensionReviewMutation.isPending
                            ? 'Resolving review...'
                            : 'Mark review resolved'}
                        </Button>
                      </div>
                      <details className='mt-2'>
                        <summary className='cursor-pointer text-xs text-muted-foreground'>
                          {`Full selection from extension (${detailQuery.data.extensionReviewCapture.selectedText.length} chars)`}
                        </summary>
                        <pre className='mt-2 max-h-48 overflow-y-auto rounded border bg-background/80 px-2 py-1 text-xs whitespace-pre-wrap'>
                          {detailQuery.data.extensionReviewCapture.selectedText}
                        </pre>
                      </details>
                    </div>
                    {detailQuery.data.extensionReviewCapture.canonicalBlocks &&
                    detailQuery.data.extensionReviewCapture.canonicalBlocks
                      .length > 0 ? (
                      <div className='space-y-2'>
                        <p className='font-medium text-foreground'>
                          Canonical text from uploaded HTML (Plan A parser)
                        </p>
                        <p className='text-xs text-muted-foreground'>
                          Snapshot from capture time when extra block detail was
                          stored with the review record.
                        </p>
                        <div className='max-h-64 space-y-1 overflow-y-auto rounded border bg-card p-2'>
                          {detailQuery.data.extensionReviewCapture.canonicalBlocks.map(
                            (block) => (
                              <p
                                key={`${block.blockId}-${block.blockOrder}`}
                                className='border-b border-border pb-1 text-xs whitespace-pre-wrap last:border-0 last:pb-0'
                              >
                                {block.rawText}
                              </p>
                            )
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {detailQuery.data.blocks.map((block, blockIndex) => (
                  <p
                    key={block.blockId}
                    data-block-id={block.blockId}
                    className={`rounded px-1 whitespace-pre-wrap ${
                      blockIndex === 0 ? 'text-lg font-semibold' : ''
                    } ${
                      effectiveHighlightedBlockIds.has(block.blockId)
                        ? 'bg-emerald-900/35 ring-1 ring-emerald-500/40'
                        : ''
                    }`}
                  >
                    {(
                      keywordDecoratedBlocks.blocksById.get(block.blockId) ?? [
                        { kind: 'plain', text: block.rawText },
                      ]
                    ).map((segment, index) => {
                      if (segment.kind === 'scope') {
                        if (!shouldShowScopeKeywordMarks) {
                          return (
                            <span key={`${block.blockId}-${index}`}>
                              {segment.text}
                            </span>
                          )
                        }
                        return (
                          <mark
                            key={`${block.blockId}-${index}`}
                            ref={(node) => {
                              scopeMatchRefs.current[segment.scopeMatchIndex] =
                                node
                            }}
                            className={`rounded px-px text-inherit ${
                              segment.scopeMatchIndex === activeScopeMatchIndex
                                ? 'bg-orange-900/45 ring-1 ring-orange-400/60'
                                : 'bg-sky-900/45 ring-1 ring-sky-400/40'
                            }`}
                          >
                            {segment.text}
                          </mark>
                        )
                      }
                      if (segment.kind === 'subjectMatter') {
                        return (
                          <mark
                            key={`${block.blockId}-${index}`}
                            className='rounded bg-violet-900/45 px-px text-inherit ring-1 ring-violet-400/40'
                          >
                            {segment.text}
                          </mark>
                        )
                      }
                      return (
                        <span key={`${block.blockId}-${index}`}>
                          {segment.text}
                        </span>
                      )
                    })}
                  </p>
                ))}
              </article>
            </div>
          )}
        </section>
      </main>
    </div>
  )
}

function toDisplayCelex(value: string): string {
  const parts = value
    .split('__')
    .map((part) => part.trim())
    .filter(Boolean)
  if (parts.length >= 2 && parts[0] === parts[1]) {
    return parts[0] ?? value
  }
  return parts[0] ?? value
}

function splitByKeywordMatches(
  text: string
): Array<
  | { kind: 'plain'; text: string }
  | { kind: 'scope'; text: string; scopeMatchIndex: number }
  | { kind: 'subjectMatter'; text: string }
> {
  const pattern = /\bsubject matter\b|\bscope\b/gi
  const segments: Array<
    | { kind: 'plain'; text: string }
    | { kind: 'scope'; text: string; scopeMatchIndex: number }
    | { kind: 'subjectMatter'; text: string }
  > = []

  let cursor = 0
  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0
    const matchText = match[0] ?? ''
    const end = start + matchText.length
    if (start > cursor) {
      segments.push({
        kind: 'plain',
        text: text.slice(cursor, start),
      })
    }
    if (/^subject matter$/i.test(matchText)) {
      segments.push({
        kind: 'subjectMatter',
        text: matchText,
      })
    } else {
      segments.push({
        kind: 'scope',
        text: matchText,
        scopeMatchIndex: -1,
      })
    }
    cursor = end
  }

  if (cursor < text.length) {
    segments.push({
      kind: 'plain',
      text: text.slice(cursor),
    })
  }

  if (segments.length === 0) {
    return [{ kind: 'plain', text }]
  }

  return segments
}

function encodeTierSelection(tiers: Array<1 | 2 | 3>): string {
  if (tiers.length === 0) {
    return 'none'
  }
  return tiers.join(',')
}

function normalizeTagInput(value: string): string | null {
  const normalized = value.trim().replace(/\s+/g, ' ')
  return normalized.length > 0 ? normalized : null
}

function computeOffsetWithinBlock(
  blockElement: Element,
  container: Node,
  offset: number
): number | null {
  if (!blockElement.contains(container)) {
    return null
  }
  const range = document.createRange()
  range.selectNodeContents(blockElement)
  range.setEnd(container, offset)
  return range.toString().length
}

export function hasExpandedSelectionInsideElement(
  selection: Selection | null,
  element: Element
): boolean {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return false
  }
  const anchorNode = selection.anchorNode
  const focusNode = selection.focusNode
  if (!anchorNode || !focusNode) {
    return false
  }
  return element.contains(anchorNode) && element.contains(focusNode)
}
